import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PatternlibButton,
  PatternlibTextinput,
  PatternlibTextlink,
  PatternlibIcon,
  PatternlibPill,
  PatternlibTooltip,
  PatternlibCard,
} from "@liebherr/patternlib-react";
import { useTranslation } from "react-i18next";
import styles from "./Start.module.css";
import keyIcon from "../../img/liebherr-key-icon.svg";
import MainNavigation from "../../components/main-navigation/MainNavigation";
import InfoButton from "../../components/info-button/InfoButton";
import image260bar from "../../img/260bar.png";
import image380bar from "../../img/380bar.png";
import imageIso6022 from "../../img/iso6022.png";
import { pushToDataLayer } from "../../helpers/helpers";
import Nav from "../../components/nav/Nav";
import Separator from "../../components/separator/Separator";

const Start = ({ profile, isDemoUser }) => {
  const { t, i18n } = useTranslation();
  const [inputValue, setInputValue] = useState();

  const location = useLocation();
  const navigate = useNavigate();

  //function to track the scroll depth if necessary
  /*function currentScrollPercentage() {
    return (
      ((document.documentElement.scrollTop + document.body.scrollTop) /
        (document.documentElement.scrollHeight -
          document.documentElement.clientHeight)) *
      100
    );
  }*/

  const configLoadGA = (productKey) => {
    const eventName = "configuration_load";
    const pagePath = location.pathname;
    const pageType = "home";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    const product_key = productKey;
    let props = { pagePath, pageType, language, product_key };

    pushToDataLayer(eventName, props);
  };

  const calcAssistentGA = () => {
    const eventName = "calculation_assistent";
    const pagePath = location.pathname;
    const pageType = "my_configurations";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    let props = { pagePath, pageType, language };

    pushToDataLayer(eventName, props);
  };

  // on first render: clear the session Storage with configuration settings.
  // it will be set when the user starts the configuration
  useEffect(() => {
    sessionStorage.clear();
  }, []);

  const onChangeListener = (event) => {
    if (event.detail?.value !== undefined) {
      setInputValue(event.detail.value);
    }
  };

  const searchForPart = () => {
    const input = inputValue.trim();
    const strTyp = input.substring(0, 3);
    configLoadGA(input);
    switch (strTyp) {
      case "S26":
        // console.log("Load series 260");
        navigate("/series260", { state: { key: input } });
        break;
      case "S38":
        // console.log("Load series 380");
        navigate("/series380", { state: { key: input } });
        break;
      case "ISO":
        // console.log("Load series iso 6022");
        navigate("/iso6022", { state: { key: input } });
        break;
      default:
        navigate("/series260", { state: { key: input } });
    }
  };

  const toFinder = () => {
    calcAssistentGA();
    navigate("/finder");
  };

  const configStartGA = (series) => () => {
    const eventName = "configuration_start";
    const pagePath = location.pathname;
    const pageType = "home";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    let props = { pagePath, pageType, language };

    switch (series) {
      case "series260":
        props = { ...props, series_type: "Series 260 bar" };
        pushToDataLayer(eventName, props);
        navigate("/series260");
        break;
      case "series380":
        props = { ...props, series_type: "Series 380 bar" };
        pushToDataLayer(eventName, props);
        navigate("/series380");
        break;
      case "iso6022":
        props = {
          ...props,
          series_type: "Series according to ISO 6022",
        };
        pushToDataLayer(eventName, props);
        navigate("/iso6022");
        break;
      default:
        props = { ...props, series_type: "Series 260 bar" };
        pushToDataLayer(eventName, props);
        navigate("/series260");
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.navigation}>
          <MainNavigation
            profile={profile}
            isDemoUser={isDemoUser}
          ></MainNavigation>
        </div>
      </header>
      <div className={styles.body}>
        {isDemoUser && (
          <PatternlibPill no-states label="Demo-Mode"></PatternlibPill>
        )}
        <div className={styles.start}>
          <Nav activeTab="0"></Nav>
          <Separator space="xxxxs"></Separator>
          <h2>{t("start.selectSeries")}</h2>
          <Separator space="xxxxs"></Separator>
          <ul className={styles.tiles}>
            <li>
              <div onClick={configStartGA("series380")} className={styles.card}>
                <PatternlibCard height="100%" className="">
                  <div slot="card-content">
                    <PatternlibTooltip tooltip-alignment="center">
                      <div className={styles.cardContent}>
                        <h3>{t("start.series380")}</h3>
                        <img src={image380bar} alt="img"></img>
                        <PatternlibTextlink
                          label={t("start.start")}
                          icon-position="right"
                        >
                          <PatternlibIcon
                            icon-name="arrow-right"
                            size="24px"
                            bold="true"
                            slot="icon"
                          />
                        </PatternlibTextlink>
                      </div>
                      <div slot="content" className={styles.content}>
                        {t("start.series380content")}
                      </div>
                    </PatternlibTooltip>
                  </div>
                </PatternlibCard>
              </div>
            </li>

            <li>
              <div onClick={configStartGA("series260")} className={styles.card}>
                <PatternlibCard height="100%">
                  <div slot="card-content">
                    <PatternlibTooltip tooltip-alignment="center">
                      <div className={styles.cardContent}>
                        <h3>{t("start.series260")}</h3>
                        <img src={image260bar}></img>
                        <PatternlibTextlink
                          label={t("start.start")}
                          icon-position="right"
                        >
                          <PatternlibIcon
                            icon-name="arrow-right"
                            size="24px"
                            bold="true"
                            slot="icon"
                          />
                        </PatternlibTextlink>
                      </div>
                      <div slot="content" className={styles.content}>
                        {t("start.series260content")}
                      </div>
                    </PatternlibTooltip>
                  </div>
                </PatternlibCard>
              </div>
            </li>

            <li>
              <div onClick={configStartGA("iso6022")} className={styles.card}>
                <PatternlibCard height="100%">
                  <div slot="card-content">
                    <PatternlibTooltip tooltip-alignment="center">
                      <div className={styles.cardContent}>
                        <h3>{t("start.seriesISO6022")}</h3>
                        <img src={imageIso6022}></img>
                        <PatternlibTextlink
                          label={t("start.start")}
                          icon-position="right"
                        >
                          <PatternlibIcon
                            icon-name="arrow-right"
                            size="24px"
                            bold="true"
                            slot="icon"
                          />
                        </PatternlibTextlink>
                      </div>
                      <div slot="content" className={styles.content}>
                        {t("start.seriesISO6022content")}
                      </div>
                    </PatternlibTooltip>
                  </div>
                </PatternlibCard>
              </div>
            </li>
          </ul>
          <div className={styles.separator} style={{ height: "24px" }}></div>
          <h3>{t("start.whichSeries")}</h3>
          <div className={styles.separator}></div>
          <div className={styles.buttonWrapper}>
            <PatternlibButton
              label={t("start.toAssistant")}
              size="small"
              onClick={toFinder}
            ></PatternlibButton>
          </div>
        </div>
        <div className={styles.searchContainer}>
          <div className={styles.searchHead}>
            <img src={keyIcon} width="48px"></img>
            <h3>{t("start.haveKey")}</h3>
            <InfoButton
              desc={t("KeyButton.partKey")}
              abbreviation={"KEY"}
              project={"series260"}
            ></InfoButton>
          </div>
          <p>{t("start.keyInfo")}</p>
          <div className={styles.searchWrapper}>
            <PatternlibTextinput
              placeholder={t("start.enterKey")}
              onLhChange={(e) => onChangeListener(e)}
            ></PatternlibTextinput>
            <PatternlibButton
              label={t("start.loadConfig")}
              size="small"
              onClick={searchForPart}
            ></PatternlibButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Start;
